import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useAppLocale } from '@/hooks/useAppLocale'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Quotation } from '../types/quotation'

function QuotationCard({ quotation }: { quotation: Quotation }) {
  const { t } = useTranslation()
  const locale = useAppLocale()
  const formatPrice = (price: number) => new Intl.NumberFormat('th-TH').format(price)
  const isExpired = new Date(quotation.validUntil) < new Date()
  return (
    <SectionCard
      key={quotation.id}
      data-testid={`quotation-card-${quotation.id}`}
      cardClass={`border-border/30 bg-secondary/20 shadow-none ${isExpired ? 'opacity-60' : ''}`}
      headerClass="pb-0"
      header={
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-foreground">{quotation.vendorName || '-'}</span>
          <span className="text-[10px] text-muted-foreground">
            {t('quotation.submittedAt')}{' '}
            {new Date(quotation.submittedAt).toLocaleDateString(locale, {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      }
      right={
        <div className="flex flex-col items-end">
          <span className="text-sm font-bold text-primary">{formatPrice(quotation.price)} บาท</span>
          {isExpired && (
            <Badge variant="outline" className="text-[9px] border-destructive/50 text-destructive">
              {t('common.expired')}
            </Badge>
          )}
        </div>
      }
    >
      <>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          <div className="rounded bg-secondary/50 px-2 py-1">
            <span className="text-[9px] text-muted-foreground">{t('quotation.minQuantity')}</span>
            <p className="text-xs font-medium text-foreground">{quotation.minQuantity}</p>
          </div>
          <div className="rounded bg-secondary/50 px-2 py-1">
            <span className="text-[9px] text-muted-foreground">{t('quotation.validUntil')}</span>
            <p className="text-xs font-medium text-foreground">
              {new Date(quotation.validUntil).toLocaleDateString('th-TH', { day: '2-digit', month: 'short' })}
            </p>
          </div>
          <div className="rounded bg-secondary/50 px-2 py-1">
            <span className="text-[9px] text-muted-foreground">{t('quotation.warranty')}</span>
            <p className="text-xs font-medium text-foreground">{quotation.warrantyYears} ปี</p>
          </div>
          <div className="rounded bg-secondary/50 px-2 py-1">
            <span className="text-[9px] text-muted-foreground">{t('quotation.maintenancePerYear')}</span>
            <p className="text-xs font-medium text-foreground">{formatPrice(quotation.maintenancePricePerYear)}</p>
          </div>
        </div>
        {quotation.adminNote && (
          <p className="mt-2 text-[10px] text-muted-foreground break-words">
            {t('quotation.requesterNote')}: {quotation.adminNote}
          </p>
        )}
        {quotation.note && (
          <p className={`text-[10px] text-muted-foreground break-words ${!quotation.adminNote ? 'mt-2' : ''}`}>
            {t('quotation.vendorNote')}: {quotation.note}
          </p>
        )}
      </>
    </SectionCard>
  )
}
export function QuotationList({ quotations }: { quotations: Quotation[] }) {
  const { t } = useTranslation()
  const { isAdmin } = useAuth()

  // const { latest, history } = groupLatestQuotationsByVendor(quotations)
  // const visibleQuotations = latest.filter(q => q.status === 'Submitted' || q.status === 'Expired')
  // const pendingQuatations = latest.filter(q => q.status === 'Pending')

  const activeQuotations = quotations.filter(
    q => q.status === 'Submitted' && q.validUntil && new Date(q.validUntil) >= new Date(),
  )
  const pendingQuotations = quotations.filter(q => q.status === 'Pending')
  const expiredQuotations = quotations.filter(
    q => (q.status === 'Submitted' || q.status === 'Expired') && q.validUntil && new Date(q.validUntil) < new Date(),
  )

  if (quotations.length === 0) {
    return (
      <div className="py-8 text-center">
        <FileText className="mx-auto mb-2 size-8 text-muted-foreground/50" />
        <p className="text-xs text-muted-foreground">{t('quotation.empty')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {activeQuotations.map(q => {
        return <QuotationCard key={q.id} quotation={q}></QuotationCard>
      })}

      {pendingQuotations.length > 0 && (
        <div className="mt-2 rounded-lg border border-dashed border-border/50 p-3">
          <p className="mb-2 text-[10px] font-medium text-muted-foreground">
            {t('quotation.pendingTitle', {
              count: pendingQuotations.length,
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            {pendingQuotations.map(r => {
              return (
                <Badge
                  key={r.id}
                  variant="outline"
                  className="text-[10px] text-muted-foreground break-words whitespace-normal"
                >
                  {r?.vendorName}
                </Badge>
              )
            })}
          </div>
        </div>
      )}

      {isAdmin && expiredQuotations.length > 0 && (
        <>
          <Separator />
          <p className="text-sm font-medium">{t('quotation.historyTitle')}</p>
          {expiredQuotations.map(q => {
            return <QuotationCard key={q.id} quotation={q}></QuotationCard>
          })}
        </>
      )}
    </div>
  )
}
